/**
 * 공유 3단 폴백 + 최종 종착지 (UPLIFT §8.5).
 *   1) navigator.share({ text })      — 제스처 안에서 동기 호출돼야 한다
 *   2) navigator.clipboard.writeText  — 보안 출처만
 *   3) textarea + execCommand('copy') — http://<맥IP>:5173 비보안 출처용
 *   4) 항상 보이는 readonly textarea   — 실패 경로가 없다(호출부가 'manual' 을 받으면 띄운다)
 * AbortError 는 폴백하지 않는다 → 'cancelled'. 폴백하면 "취소했는데 조용히 복사됨"이 된다.
 */
export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'manual';
export type ShareWay = 'share' | 'copy' | 'exec' | 'manual';

export interface ShareHost {
  readonly share: ((text: string) => Promise<void>) | null;
  readonly clipboardWrite: ((text: string) => Promise<void>) | null;
  readonly execCopy: ((text: string) => boolean) | null;
}

export async function shareText(
  text: string,
  host: ShareHost,
): Promise<{ outcome: ShareOutcome; way: ShareWay }> {
  if (host.share !== null) {
    try {
      await host.share(text);
      return { outcome: 'shared', way: 'share' };
    } catch (err) {
      if (isAbort(err)) return { outcome: 'cancelled', way: 'share' };
      // 그 밖의 실패(미지원·정책)는 다음 단으로
    }
  }
  if (host.clipboardWrite !== null) {
    try {
      await host.clipboardWrite(text);
      return { outcome: 'copied', way: 'copy' };
    } catch {
      /* 다음 단 */
    }
  }
  if (host.execCopy !== null) {
    try {
      if (host.execCopy(text)) return { outcome: 'copied', way: 'exec' };
    } catch {
      /* 다음 단 */
    }
  }
  return { outcome: 'manual', way: 'manual' };
}

function isAbort(err: unknown): boolean {
  return (
    typeof err === 'object' && err !== null && (err as { name?: string }).name === 'AbortError'
  );
}

/** 브라우저 호스트 — 없는 API 는 null 로 두어 그 단을 건너뛴다. */
/** 안드로이드 앱 셸이 WebView 에 심는 다리(`app/…/AjaeBridge.kt`). 있으면 공유 시트는 네이티브가 연다. */
interface NativeBridge {
  readonly share: (text: string) => void;
  readonly getPack?: (id: string, version: string) => string | null;
  readonly putPack?: (id: string, version: string, packHash: string, json: string) => void;
}
declare global {
  interface Window {
    AjaeNative?: NativeBridge;
  }
}

export function browserShareHost(): ShareHost {
  const nav = typeof navigator === 'undefined' ? null : navigator;
  const native = typeof window === 'undefined' ? undefined : window.AjaeNative;
  const canShare = nav !== null && typeof nav.share === 'function';
  const canClip =
    nav !== null &&
    typeof nav.clipboard === 'object' &&
    nav.clipboard !== null &&
    typeof nav.clipboard.writeText === 'function' &&
    typeof window !== 'undefined' &&
    window.isSecureContext === true;
  return {
    share:
      native !== undefined
        ? (text) => {
            native.share(text);
            return Promise.resolve();
          }
        : canShare
          ? (text) => (nav as Navigator).share({ text })
          : null,
    clipboardWrite: canClip ? (text) => (nav as Navigator).clipboard.writeText(text) : null,
    execCopy:
      typeof document === 'undefined'
        ? null
        : (text) => {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.top = '-1000px';
            document.body.append(ta);
            ta.select();
            let ok = false;
            try {
              ok = document.execCommand('copy');
            } finally {
              ta.remove();
            }
            return ok;
          },
  };
}
